import { OrgTypes } from '@mode/shared/contract-common';
import { FeatureFlagsFacade } from '@mode/shared/contract-common';
import * as angular from 'angular';
import { combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { MoveSpaceModalService } from '~/components/modal-move-space/move-space-modal.service';
import { AppStore } from '~/types/main.types';
import { SpaceHelpers } from '@mode/legacy/data-access-model-js';
import { AnalyticsService, RunMethodType, UPSELL_INTERACTION } from '@mode/shared/util-segment';
import { FeatureToggleService } from '../../services/feature-toggle-service';
import { AccountFacade } from '@mode/accounts/contract';
import { MoveSpacePublishService } from '@mode/dataset/domain';
export default class MoveSpaceModalController {
  public spaces: any[];
  public query = { name: '' };
  public spacesAccessDetails: {};
  private owner: OrgTypes.Organization;
  private report: any;
  private $close: any;
  private $dismiss: any;
  private currSpace: any;
  private upsellEnabled$: Observable<boolean>;
  private spaceUpsellEnabled$: Observable<boolean>;
  private emptyStateUpsellEnabled$: Observable<boolean>;
  private spaces$: BehaviorSubject<any[]>;
  private isViaPublishAction: boolean;
  private switchViewToken: string;

  // used in template
  private isDatasetEditor: boolean;
  private isDatasetPublished: boolean;

  static $inject = [
    'FeatureToggleService',
    '$scope',
    '$state',
    'AnalyticsService',
    'RxStoreService',
    'MoveSpaceModalService',
    'FeatureFlagsFacade',
    'AccountFacade',
    'MoveSpacePublishService',
  ];

  constructor(
    private featureToggleService: FeatureToggleService,
    private $scope: angular.IScope,
    private $state,
    private analyticsService: AnalyticsService,
    private rxStoreService: AppStore,
    private moveSpaceModalService: MoveSpaceModalService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private accountFacade: AccountFacade,
    private moveSpacePublishService: MoveSpacePublishService
  ) {
    this.spaces$ = new BehaviorSubject(void 0);
    this.$scope.$watch('ctrl.spaces', (spaces: any[]) => this.spaces$.next(spaces));
  }

  $onInit() {
    let product_context = '';

    if (this.$state.includes('root.space')) {
      product_context = 'space';
    } else if (this.$state.includes('root')) {
      product_context = 'home';
    } else if (this.$state.includes('application.reports.edit')) {
      product_context = 'report_editor';
    }

    this.isDatasetEditor = this.$state.includes('application.datasetReports.edit') ?? false;

    this.upsellEnabled$ = this.featureToggleService.featureBusinessUpsellEnabled$;
    this.spaceUpsellEnabled$ = combineLatest([this.upsellEnabled$, this.spaces$]).pipe(
      // only enable if there are also 1 or more spaces
      map(([enabled, spaces]) => enabled && spaces && spaces.length > 0)
    );

    this.emptyStateUpsellEnabled$ = combineLatest([this.upsellEnabled$, this.spaces$]).pipe(
      // only enable if there are no more spaces
      map(([enabled, spaces]) => enabled && spaces && spaces.length === 0)
    );

    this.spaceUpsellEnabled$
      .pipe(
        filter((e) => e !== void 0),
        first()
      )
      .subscribe((enabled) => {
        if (enabled) {
          this.analyticsService.run(RunMethodType.Track, UPSELL_INTERACTION, {
            product_context,
            product_subcontext: 'report_move_modal',
            interaction_type: 'view',
            upsell_element_id: 3,
            organization_id: this.owner.id,
          });
        }
      });

    this.emptyStateUpsellEnabled$
      .pipe(
        filter((e) => e !== void 0),
        first()
      )
      .subscribe((enabled) => {
        if (enabled) {
          this.analyticsService.run(RunMethodType.Track, UPSELL_INTERACTION, {
            product_context,
            product_subcontext: 'report_move_modal',
            interaction_type: 'view',
            upsell_element_id: 4,
            organization_id: this.owner.id,
          });
        }
      });

    if (!this.spaces) {
      this.moveSpaceModalService
        .getCustomSpaces(this.owner)
        // users can sometimes open move-space model from personal collections
        // so we need to ensure it's in the array
        .then((spaces: any[]) => this.moveSpaceModalService.ensureReportCollection(spaces, this.report, this.owner))
        .then((spaces: any[]) => {
          this.$scope.$applyAsync(async () => {
            this.spaces = this.moveSpaceModalService.getFilteredAndSortedSpaces(spaces, this.report);
            this.spacesAccessDetails = this.moveSpaceModalService.getSpacesAccessDetails(this.spaces);
            this.currSpace = await this.report.space();
          });
        });
    } else {
      this.spacesAccessDetails = this.moveSpaceModalService.getSpacesAccessDetails(this.spaces);
    }
    this.isDatasetPublished = this.moveSpaceModalService.isDatasetReportPublished(this.report);
  }

  trialLink() {
    const url = this.owner.links.trial;
    return url ? url + '?trk_element_id=3' : url;
  }

  spacesAreSearchable() {
    return this.spaces && this.spaces.length > 4;
  }

  canCreateSpace() {
    return SpaceHelpers.canCreate(this.currSpace && this.currSpace._resource);
  }

  getCollectionIcon(collection): string {
    return SpaceHelpers.getCollectionIcon(collection);
  }

  moveReport($space) {
    this.$close({ $space, $noMove: true, $currSpace: this.currSpace });
  }

  reportInPersonalCollection() {
    const currentCollectionResource = this.currSpace?._resource;
    return SpaceHelpers.isPrivate(currentCollectionResource);
  }

  hidePermissionWarning(selectedSpace): boolean {
    return !selectedSpace;
  }

  handleQueryChanged(event) {
    this.query.name = event;
  }

  handleSkip() {
    this.$dismiss();
    this.moveSpacePublishService.publishDatasetAfterMoveSpace(this.switchViewToken);
  }
}
